import { SystemStatusAppliance } from '@ekkogmbh/apisdk';
import { Color, Grid } from '@mui/material';
import Chip from '@mui/material/Chip';
import { green, orange, red } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { saturation } from '../Styles/SystemStatusStyles';
import { ApplianceTitle } from './ApplianceTitle';
import SystemStatusBoxCard from './SystemStatusBoxCard';

interface DataObject {
  name: string;
  value: number;
  color: Color;
}

interface Props {
  appliance: SystemStatusAppliance;
}

export default function SystemStatusBox({ appliance }: Props): React.JSX.Element {
  const labelStatus = useMemo(() => {
    if (appliance.information.summaryOfLabels?.status === undefined) {
      return undefined;
    }

    return appliance.information.summaryOfLabels.status as {
      successfulTagCount?: number;
      processingTagCount?: number;
      timeoutTagCount?: number;
    };
  }, [appliance.information.summaryOfLabels?.status]);

  /** -- ad-hoc typed data extraction -- */
  const batteryStatus = useMemo(() => {
    if (appliance.information.summaryOfLabels?.batteryStatus === undefined) {
      return undefined;
    }

    return appliance.information.summaryOfLabels.batteryStatus as {
      goodBatteryTagCount?: number;
      badBatteryTagCount?: number;
    };
  }, [appliance.information.summaryOfLabels?.batteryStatus]);

  const signalStrength = useMemo(() => {
    if (appliance.information.summaryOfLabels?.signalStrength === undefined) {
      return undefined;
    }

    return appliance.information.summaryOfLabels.signalStrength as {
      excellentSignalTagCount?: number;
      goodSignalTagCount?: number;
      badSignalTagCount?: number;
    };
  }, [appliance.information.summaryOfLabels?.signalStrength]);

  const summaryOfAccessPoints = useMemo(() => {
    if (appliance.information.summaryOfAccessPoints === undefined) {
      return undefined;
    }

    return appliance.information.summaryOfAccessPoints as {
      totalAccessPointCount?: number;
      connectedAccessPointCount?: number;
    };
  }, [appliance.information.summaryOfAccessPoints]);

  /** -- data aggregation --  */
  const accessPointData = useMemo((): DataObject[] => {
    if (summaryOfAccessPoints === undefined) {
      return [];
    }

    const disconnectedCount = Math.max(
      0,
      (summaryOfAccessPoints.totalAccessPointCount ?? 0) - (summaryOfAccessPoints.connectedAccessPointCount ?? 0),
    );

    return [
      {
        name: 'connected',
        value: summaryOfAccessPoints.connectedAccessPointCount ?? 0,
        color: green,
      },
      {
        name: 'disconnected',
        value: disconnectedCount,
        color: red,
      },
    ];
  }, [summaryOfAccessPoints]);

  const labelData = useMemo(
    (): DataObject[] => [
      {
        name: 'successful',
        value: labelStatus?.successfulTagCount ?? 0,
        color: green,
      },
      {
        name: 'processing',
        value: labelStatus?.processingTagCount ?? 0,
        color: orange,
      },
      {
        name: 'timeout',
        value: labelStatus?.timeoutTagCount ?? 0,
        color: red,
      },
    ],
    [labelStatus],
  );

  const batteryData = useMemo(
    (): DataObject[] => [
      {
        name: 'good',
        value: batteryStatus?.goodBatteryTagCount ?? 0,
        color: green,
      },
      {
        name: 'bad',
        value: batteryStatus?.badBatteryTagCount ?? 0,
        color: red,
      },
    ],
    [batteryStatus],
  );

  const signalData = useMemo(
    (): DataObject[] => [
      {
        name: 'excellent',
        value: signalStrength?.excellentSignalTagCount ?? 0,
        color: green,
      },
      {
        name: 'good',
        value: signalStrength?.goodSignalTagCount ?? 0,
        color: orange,
      },
      {
        name: 'bad',
        value: signalStrength?.badSignalTagCount ?? 0,
        color: red,
      },
    ],
    [signalStrength],
  );

  const applianceData = useMemo(
    () => [
      {
        title: 'Labels',
        data: labelData,
      },
      {
        title: 'Battery',
        data: batteryData,
      },
      {
        title: 'Signal-Strength',
        data: signalData,
      },
      {
        title: 'AccessPoints',
        data: accessPointData,
      },
    ],
    [labelData, batteryData, signalData, accessPointData],
  );

  return (
    <Grid container spacing={2} alignContent={'space-between'} alignItems={'stretch'}>
      <Grid item xs={9}>
        <ApplianceTitle appliance={appliance} />
      </Grid>

      <Grid item xs={3}>
        <Chip
          label={appliance.status}
          sx={{
            fontSize: 18,
            fontWeight: 700,
            float: 'right',
            color: (theme) => theme.palette.common.white,
            backgroundColor: appliance.status.toUpperCase() === 'ONLINE' ? green[saturation] : red[saturation],
          }}
        />
      </Grid>

      <Grid item xs={12} style={{ marginBottom: 32 }}>
        <Typography component={'p'} color="textSecondary">
          Base Url: {appliance.technology.baseUrl}
        </Typography>
      </Grid>

      {applianceData.map(({ data, title }, index) => (
        <Grid key={index} item lg={3} xs={6}>
          <SystemStatusBoxCard title={title} data={data} />
        </Grid>
      ))}
    </Grid>
  );
}
